import { useState, useMemo, useEffect } from 'react';
import { ShoppingCart, Check, Heart } from 'lucide-react';
import type { ProductSearchResult } from '../../services/products/products.service';
import { getFirstProductImage } from '../../utils/product-images';
import { capitalizeProductName } from '../../utils/capitalize';

interface ProductCardProps {
  producto: ProductSearchResult;
  theme: 'light' | 'dark';
  added?: boolean;
  isFavorite?: boolean;
  onAddToCart?: (producto: ProductSearchResult, presentacionId: string, presentacionNombre: string) => void;
  onToggleFavorite?: (producto: ProductSearchResult) => void;
}

/** Si el producto tiene Unidad + Blister + Caja, oculta Unidad.
 *  Si solo tiene Unidad (productos unitarios), se deja. */
function filtrarPresentaciones(presentaciones: ProductSearchResult['presentaciones']) {
  if (!presentaciones || presentaciones.length <= 1) return presentaciones;

  const nombres = presentaciones.map(p => p.nombre.toLowerCase().trim());
  const tieneUnidad = nombres.includes('unidad');
  const tieneBlister = nombres.includes('blister');
  const tieneCaja = nombres.includes('caja');

  if (tieneUnidad && tieneBlister && tieneCaja) {
    return presentaciones.filter(p => p.nombre.toLowerCase().trim() !== 'unidad');
  }

  return presentaciones;
}

const getIconoCategoria = (categoria: string) => {
  const iconos: Record<string, string> = {
    'medicamentos': '💊',
    'cuidado-personal': '🧴',
    'higiene-femenina': '🌸',
    'salud-sexual': '💕',
    'vitaminas-y-suplementos': '🍊',
    'bebes': '👶',
    'equipos-medicos': '🏥',
  };
  return iconos[categoria] || '📦';
};

export const ProductCard: React.FC<ProductCardProps> = ({
  producto,
  theme,
  added = false,
  isFavorite = false,
  onAddToCart,
  onToggleFavorite,
}) => {
  const presentacionesVisibles = useMemo(
    () => filtrarPresentaciones(producto.presentaciones),
    [producto.presentaciones]
  );

  const [presSeleccionada, setPresSeleccionada] = useState<string>(
    presentacionesVisibles?.[0]?.id || ''
  );

  // Si la presentación seleccionada ya no está entre las visibles, resetear
  useEffect(() => {
    const aunVisible = presentacionesVisibles?.some(p => p.id === presSeleccionada);
    if (!aunVisible && presentacionesVisibles && presentacionesVisibles.length > 0) {
      setPresSeleccionada(presentacionesVisibles[0].id);
    }
  }, [presentacionesVisibles, presSeleccionada]);

  const pres = presentacionesVisibles?.find(p => p.id === presSeleccionada)
    || presentacionesVisibles?.[0];

  const handleAdd = () => {
    if (!pres || !onAddToCart) return;
    onAddToCart(producto, pres.id, pres.nombre);
  };

  const img = getFirstProductImage(producto.imagenes);
  const noDisponible = producto.stockBase === 0;

  return (
    <div
      className={`group rounded-2xl overflow-hidden transition-all duration-300 flex flex-col h-full ${
        noDisponible
          ? 'opacity-60 grayscale'
          : 'hover:scale-[1.02]'
      } ${
        theme === 'dark'
          ? 'bg-gray-800'
          : 'bg-white shadow-md hover:shadow-xl'
      }`}
    >
      {/* Imagen */}
      <div className="relative h-40 sm:h-44 lg:h-48 bg-white overflow-hidden flex items-center justify-center">
        {noDisponible && (
          <>
            <span className="absolute top-2 left-2 z-10 bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded">
              No disponible
            </span>
            <div className="absolute inset-0 bg-gray-200/60 z-[5]" />
          </>
        )}
        {producto.requiereReceta && (
          <span className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center">
            <span className="w-1.5 h-1.5 bg-white rounded-full mr-1"></span>
            Receta
          </span>
        )}
        {img ? (
          <img
            src={img}
            alt={producto.nombre}
            className="w-full h-full object-contain p-2"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!
                .querySelector('.fallback-icon')!
                .classList.remove('hidden');
            }}
          />
        ) : null}
        <div
          className={`fallback-icon text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-center absolute inset-0 flex items-center justify-center ${
            img ? 'hidden' : ''
          }`}
        >
          {getIconoCategoria(producto.categoria)}
        </div>

        {/* Wishlist */}
        {onToggleFavorite && !noDisponible && (
          <button
            onClick={() => onToggleFavorite(producto)}
            className="absolute bottom-2 right-2 z-10 p-1.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
          >
            <Heart
              className={`w-4 h-4 ${
                isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400 hover:text-red-500'
              }`}
            />
          </button>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col flex-1">
        <div className="flex-1">
          <p
            className={`text-xs uppercase tracking-wide mb-1 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {producto.categoria}
          </p>
          <h3
            className={`font-semibold mb-1 line-clamp-2 text-sm min-h-[2.5rem] ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            {capitalizeProductName(producto.nombre)}
          </h3>

          {/* Selector de presentación */}
          {presentacionesVisibles && presentacionesVisibles.length > 1 && (
            <div className="flex gap-1.5 mb-2">
              {presentacionesVisibles.map((p) => {
                const isSelected = presSeleccionada === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPresSeleccionada(p.id)}
                    className={`flex-1 py-1 px-1.5 rounded-md text-xs font-medium border transition-all ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-600'
                        : theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="block">{p.nombre}</span>
                    <span
                      className={`block text-[10px] font-bold ${
                        isSelected ? 'text-emerald-600' : 'text-gray-400'
                      }`}
                    >
                      ${p.precio.toLocaleString()}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Precio + Botón siempre alineado al fondo */}
        <div className="mt-auto pt-2">
          <div className="mb-2">
            <span className={`font-bold text-base ${noDisponible ? 'text-gray-400' : 'text-blue-500'}`}>
              ${(pres?.precio || 0).toLocaleString()}
            </span>
          </div>

          {noDisponible ? (
            <button
              disabled
              className="w-full py-2.5 rounded-lg font-medium bg-gray-200 text-gray-400 cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <span className="text-xs sm:text-sm">No disponible</span>
            </button>
          ) : (
            <button
              onClick={handleAdd}
              disabled={added}
              className={`w-full py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 ${
                added
                  ? 'bg-green-500 text-white'
                  : 'bg-gradient-to-r from-emerald-500 to-blue-500 hover:opacity-90 text-white'
              }`}
            >
              {added ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Agregado</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Agregar</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
